/**
 * Shared client-side helper for kicking off the Amazon Seller Central SP-API
 * OAuth 2.0 flow.
 *
 * Used by the IntegrationsPage Amazon card's "Connect a seller account"
 * button.
 *
 * Unlike QBO / GA4 — where the Client ID is a single `VITE_*` env baked in
 * at build time — Amazon Application IDs and LWA Client IDs live per-org on
 * `integrations.metadata.application_id` / `metadata.client_id`. The caller
 * passes them in, and `amazon-oauth-callback` uses the same pair for code
 * exchange (no "two installs for the same org with different apps" failure
 * mode).
 *
 * CSRF defence: we stash a random state token in sessionStorage. The
 * matching `state` is validated by `AmazonCallbackPage` after Amazon
 * redirects back, and again server-side by `amazon-oauth-callback`.
 *
 * Reference: https://developer-docs.amazon.com/sp-api/docs/website-authorization-workflow
 */

export const AMAZON_OAUTH_STATE_KEY = 'amazon_oauth_state';
export const AMAZON_OAUTH_MARKETPLACE_KEY = 'amazon_oauth_marketplace_id';
export const AMAZON_OAUTH_REGION_KEY = 'amazon_oauth_region';
export const AMAZON_REDIRECT_PATH = '/integrations/amazon/callback';

/**
 * Returns the redirect_uri Amazon will send the user back to after they
 * approve the consent. This MUST be registered under your SP-API app's
 * "Login with Amazon → Allowed Return URLs" in Seller Central →
 * Apps & Services → Develop Apps. Amazon does an exact string match.
 */
export function amazonRedirectUri(): string {
  return `${window.location.origin}${AMAZON_REDIRECT_PATH}`;
}

export type AmazonRegion = 'na' | 'eu' | 'fe';

export interface AmazonMarketplace {
  id: string;
  country: string;
  label: string;
  region: AmazonRegion;
}

/**
 * The marketplaces SP-API currently supports. Keep in sync with the
 * matching map in `amazon-oauth-callback`. We deliberately surface
 * country names instead of marketplace IDs in the UI; sellers know
 * "I'm on Amazon.de" but not "A1PA6795UKMFR9".
 */
export const AMAZON_MARKETPLACES: AmazonMarketplace[] = [
  { id: 'ATVPDKIKX0DER', country: 'US', label: 'United States (amazon.com)', region: 'na' },
  { id: 'A2EUQ1WTGCTBG2', country: 'CA', label: 'Canada (amazon.ca)', region: 'na' },
  { id: 'A1AM78C64UM0Y8', country: 'MX', label: 'Mexico (amazon.com.mx)', region: 'na' },
  { id: 'A2Q3Y263D00KWC', country: 'BR', label: 'Brazil (amazon.com.br)', region: 'na' },
  { id: 'A1F83G8C2ARO7P', country: 'UK', label: 'United Kingdom (amazon.co.uk)', region: 'eu' },
  { id: 'A1PA6795UKMFR9', country: 'DE', label: 'Germany (amazon.de)', region: 'eu' },
  { id: 'A13V1IB3VIYZZH', country: 'FR', label: 'France (amazon.fr)', region: 'eu' },
  { id: 'APJ6JRA9NG5V4', country: 'IT', label: 'Italy (amazon.it)', region: 'eu' },
  { id: 'A1RKKUPIHCS9HS', country: 'ES', label: 'Spain (amazon.es)', region: 'eu' },
  { id: 'A1805IZSGTT6HS', country: 'NL', label: 'Netherlands (amazon.nl)', region: 'eu' },
  { id: 'A2NODRKZP88ZB9', country: 'SE', label: 'Sweden (amazon.se)', region: 'eu' },
  { id: 'A1C3SOZRARQ6R3', country: 'PL', label: 'Poland (amazon.pl)', region: 'eu' },
  { id: 'A33AVAJ2PDY3EV', country: 'TR', label: 'Turkey (amazon.com.tr)', region: 'eu' },
  { id: 'A17E79C6D8DWNP', country: 'SA', label: 'Saudi Arabia (amazon.sa)', region: 'eu' },
  { id: 'A2VIGQ35RCS4UG', country: 'AE', label: 'UAE (amazon.ae)', region: 'eu' },
  { id: 'A21TJRUUN4KGV', country: 'IN', label: 'India (amazon.in)', region: 'eu' },
  { id: 'A19VAU5U5O7RUS', country: 'SG', label: 'Singapore (amazon.sg)', region: 'fe' },
  { id: 'A39IBJ37TRP1C6', country: 'AU', label: 'Australia (amazon.com.au)', region: 'fe' },
  { id: 'A1VC38T7YXB528', country: 'JP', label: 'Japan (amazon.co.jp)', region: 'fe' },
];

/**
 * Seller Central consent-screen hostnames per marketplace. Amazon's
 * `apps/authorize/consent` endpoint must be hit on the Seller Central
 * regional domain matching the seller's marketplace — sending an EU seller
 * to `sellercentral.amazon.com` makes them sign in to their (non-existent)
 * NA account, which is the #1 cause of "Amazon won't redirect me back"
 * support tickets.
 */
const CONSENT_HOST: Record<string, string> = {
  ATVPDKIKX0DER: 'sellercentral.amazon.com',
  A2EUQ1WTGCTBG2: 'sellercentral.amazon.ca',
  A1AM78C64UM0Y8: 'sellercentral.amazon.com.mx',
  A2Q3Y263D00KWC: 'sellercentral.amazon.com.br',
  A1F83G8C2ARO7P: 'sellercentral.amazon.co.uk',
  A1PA6795UKMFR9: 'sellercentral.amazon.de',
  A13V1IB3VIYZZH: 'sellercentral.amazon.fr',
  APJ6JRA9NG5V4: 'sellercentral.amazon.it',
  A1RKKUPIHCS9HS: 'sellercentral.amazon.es',
  A1805IZSGTT6HS: 'sellercentral.amazon.nl',
  A2NODRKZP88ZB9: 'sellercentral.amazon.se',
  A1C3SOZRARQ6R3: 'sellercentral.amazon.pl',
  A33AVAJ2PDY3EV: 'sellercentral.amazon.com.tr',
  A17E79C6D8DWNP: 'sellercentral.amazon.sa',
  A2VIGQ35RCS4UG: 'sellercentral.amazon.ae',
  A21TJRUUN4KGV: 'sellercentral.amazon.in',
  A19VAU5U5O7RUS: 'sellercentral.amazon.sg',
  A39IBJ37TRP1C6: 'sellercentral.amazon.com.au',
  A1VC38T7YXB528: 'sellercentral.amazon.co.jp',
};

export function marketplaceById(id: string): AmazonMarketplace | undefined {
  return AMAZON_MARKETPLACES.find((m) => m.id === id);
}

export interface StartAmazonOAuthArgs {
  /** SP-API Application ID (`amzn1.sp.solution.…`) from per-org metadata. */
  applicationId: string;
  /** Marketplace the seller wants to authorize (drives both the consent host and the region pinned on the channel). */
  marketplaceId: string;
  /**
   * If the app is still in DRAFT in Seller Central (i.e. you haven't
   * submitted it for publication), Amazon requires `version=beta` on the
   * consent URL. Published apps reject the parameter. Most BYO Atlas
   * installs are draft — they only ever authorize the operator's own
   * seller accounts — so this defaults to true.
   */
  draft?: boolean;
}

/**
 * Begins the OAuth flow by redirecting the browser to Seller Central.
 * Returns `null` on success (the caller's tab is about to navigate away)
 * and a human-readable error string if validation fails. Callers should
 * surface the error via toast.
 */
export function startAmazonOAuth(args: StartAmazonOAuthArgs): string | null {
  const applicationId = args.applicationId?.trim();
  if (!applicationId) {
    return 'No Amazon Application ID configured. Configure app credentials first.';
  }
  if (!/^amzn1\.sp\.solution\./i.test(applicationId)) {
    return 'Application ID looks malformed (expected `amzn1.sp.solution.<id>`). Reconfigure app credentials.';
  }

  const marketplace = marketplaceById(args.marketplaceId);
  if (!marketplace) {
    return `Unknown marketplace "${args.marketplaceId}". Pick one from the dropdown.`;
  }

  const consentHost = CONSENT_HOST[marketplace.id];
  if (!consentHost) {
    return `No Seller Central consent host mapped for ${marketplace.label}. Open a ticket.`;
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(AMAZON_OAUTH_STATE_KEY, state);
  sessionStorage.setItem(AMAZON_OAUTH_MARKETPLACE_KEY, marketplace.id);
  sessionStorage.setItem(AMAZON_OAUTH_REGION_KEY, marketplace.region);

  const url = new URL(`https://${consentHost}/apps/authorize/consent`);
  url.searchParams.set('application_id', applicationId);
  url.searchParams.set('state', state);
  url.searchParams.set('redirect_uri', amazonRedirectUri());
  // Draft apps MUST include `version=beta`; published apps MUST NOT.
  if (args.draft ?? true) {
    url.searchParams.set('version', 'beta');
  }

  window.location.href = url.toString();
  return null;
}
